import { useState } from "react";
import { PopupModal } from "../../../components/Modal";
import type { MultiplierConfig } from "../types";
import { useUpdatePricingConfig } from "../hooks";

function formatValue(value: unknown): string {
  if (value === null) return "disabled";
  if (typeof value === "boolean") return value ? "on" : "off";
  return String(value);
}

function getConfigDiff(
  original: MultiplierConfig,
  edited: MultiplierConfig,
): Array<{ path: string; oldVal: unknown; newVal: unknown }> {
  const diffs: Array<{ path: string; oldVal: unknown; newVal: unknown }> = [];

  const compare = (
    orig: Record<string, any>,
    edit: Record<string, any>,
    prefix: string,
  ) => {
    for (const key of Object.keys(edit)) {
      const origVal = orig[key];
      const editVal = edit[key];
      const path = prefix ? `${prefix}.${key}` : key;

      if (
        typeof editVal === "object" &&
        editVal !== null &&
        !Array.isArray(editVal)
      ) {
        compare(origVal || {}, editVal, path);
      } else if (origVal !== editVal) {
        diffs.push({ path, oldVal: origVal, newVal: editVal });
      }
    }
  };

  compare(original as any, edited as any, "");
  return diffs;
}

export function SaveConfigModal({
  originalConfig,
  editedConfig,
  userId,
  onClose,
}: {
  originalConfig: MultiplierConfig;
  editedConfig: MultiplierConfig;
  userId: string;
  onClose: () => void;
}) {
  const [reasoning, setReasoning] = useState("");
  const updateMutation = useUpdatePricingConfig();
  const diffs = getConfigDiff(originalConfig, editedConfig);

  const handleConfirm = () => {
    const flatChanges: Record<string, number> = {};
    for (const d of diffs) {
      flatChanges[d.path] = d.newVal as number;
    }

    updateMutation.mutate(
      {
        userId,
        data: {
          changes: flatChanges as any,
          scope: { type: "base" },
          reasoning,
          trigger: "admin",
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="mx-auto max-w-[500px] w-full"
    >
      <h3 className="text-lg font-semibold text-content-primary">
        Confirm Changes
      </h3>

      <div className="max-h-[200px] overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-content-tertiary">
              <th className="pb-1 font-medium">Setting</th>
              <th className="pb-1 font-medium">Before</th>
              <th className="pb-1 font-medium">After</th>
            </tr>
          </thead>
          <tbody>
            {diffs.map((d) => (
              <tr key={d.path} className="border-t border-gray-100">
                <td className="py-1.5 font-medium text-content-primary">
                  {d.path}
                </td>
                <td className="py-1.5 text-red-600">
                  {formatValue(d.oldVal)}
                </td>
                <td className="py-1.5 text-green-600">
                  {formatValue(d.newVal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {diffs.length === 0 && (
          <p className="text-sm text-content-tertiary py-2">No changes detected</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-content-primary mb-1">
          Reasoning <span className="text-red-500">*</span>
        </label>
        <textarea
          value={reasoning}
          onChange={(e) => setReasoning(e.target.value)}
          className="w-full h-[80px] px-3 py-2 border border-border-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          placeholder="Why are you making this change?"
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={
            !reasoning.trim() ||
            diffs.length === 0 ||
            updateMutation.isPending
          }
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded hover:opacity-90 disabled:opacity-50"
        >
          {updateMutation.isPending ? "Saving..." : "Confirm"}
        </button>
      </div>
    </PopupModal>
  );
}
